import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WestminsterCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Westminster Pothole Claims | 1 Million Daytime Users, 339km of Road | Fixtyer"
        description="Westminster City Council maintains a £3.6 billion highway network for over one million daytime users on just 339km of road. DfT rates condition AMBER despite GREEN spend. See what that means for your pothole claim."
        canonical="/councils/westminster"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Westminster', url: '/councils/westminster' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Westminster projects capital spend at 34 times its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and whether Westminster had reasonable notice of it — not on aggregate investment. Westminster is AMBER overall on condition, and Section 58 asks whether the pothole that damaged your vehicle was reasonably inspected and repaired, not whether the council outspends its DfT grant in total.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 285km — 84% of Westminster\'s 339km adopted road network. The council publishes only the RED percentage for U-roads: 6% in 2024, up from 4% in 2021 and 2022. That is roughly 17km of residential and estate streets the council\'s own SCANNER data says should be considered for maintenance.',
          },
          {
            question: 'Pothole repairs fell from 525 to 356 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Westminster still filled 356 potholes in 2024/25 — nearly one every day — after a peak of 525 in 2023/24. The council also runs safety and service inspections simultaneously at risk-based frequencies, and has introduced a Minor Works programme for clusters of defects. A lower published pothole count does not prove your specific defect was known and repaired in time.',
          },
          {
            question: 'Why is best practice AMBER when Westminster publishes a HIAMP and Value Management process?',
            answer: 'Westminster\'s transparency report describes WMHI adoption since 2019, a formal Value Management process, AI carriageway condition assessments and an intelligence-led asset data approach. Yet the DfT Best Practice scorecard for 2025/26 is AMBER — the same split seen at other well-documented authorities where published process does not automatically translate into a GREEN rating. Your claim should still focus on the specific defect, not the council\'s policy documents alone.',
          },
          {
            question: 'Was my road on Westminster\'s 2025/26 planned carriageway programme?',
            answer: 'Westminster publishes a provisional carriageways improvement programme listing roads, wards, Value Management scores and estimated costs — from Curzon Street (£42,000) to Elizabeth Street (£156,000). If your pothole was on a street not listed, the council may argue it prioritised higher-scoring routes. If it was listed, ask whether works completed before your incident or only after damage occurred.',
          },
          {
            question: 'Does Westminster maintain every road in the borough?',
            answer: 'No. Westminster\'s linked Highway Infrastructure Asset Management Plan states it does not apply to roads managed by adjacent authorities including Transport for London, or privately owned roads. Central London has many TfL red routes. Check who maintains the road where you were damaged before you claim — defects on TfL routes go to TfL, not Westminster City Council.',
          },
          {
            question: 'How long do I have to make a claim?',
            answer: 'You generally have three years from the date of the incident under the Limitation Act 1980, but we recommend claiming within three months while evidence is fresh and the defect may still be visible.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Westminster City Council"
        councilSlug="westminster"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/westminster"
        headline="Westminster City Council Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Westminster</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Westminster: One Million Daytime Users on 339km of Road
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Westminster supports the largest centre of employment in the UK, with a daytime population of over
            one million on a compact <strong className="text-white">339km</strong> network valued at approximately
            <strong className="text-amber-400"> £3.6 billion</strong>. The council earns a GREEN spend scorecard
            and projects <strong className="text-emerald-400">84% preventative maintenance</strong> in 2025/26 —
            yet the DfT rates Westminster <strong className="text-amber-400">AMBER</strong> overall on condition
            and best practice.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">285km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 84% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              Westminster's own 2024 survey data puts 6% of U-roads in RED condition — roughly 17km of
              residential streets where maintenance should be considered.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Central London Network */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Densest Highway Network in Britain
          </h2>
          <p className="text-surface-600 mb-8">
            Scale and context from Westminster's own transparency report — published 25 June 2025
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A-roads</td>
                  <td className="px-6 py-4 text-center font-mono">48</td>
                  <td className="px-6 py-4 text-center">14.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B, C and D roads</td>
                  <td className="px-6 py-4 text-center font-mono">6</td>
                  <td className="px-6 py-4 text-center">1.8%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">U-roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold text-amber-700">285</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">84.1%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">339</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-600 text-sm">
            Westminster's report states the borough contains 40% of London's hotel beds, hosts government and national
            ceremonies, supports traffic entirely within a 20mph zone, and maintains 597km of footways, 75 highway
            structures and over 14,800 street lights alongside its 339km of carriageways. Highways are valued at
            approximately £3.6 billion — the council's largest public asset — yet the DfT still rates overall condition AMBER.
          </p>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Westminster's own transparency report — classified roads
            improving, U-road RED share edging up
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (48km — 14.2% of network): improving</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">5%</td>
                    <td className="px-4 py-2 text-center font-bold">18%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">77%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED share fell from 7% to 5% over five years, and green-condition A-roads rose from 70% to 77%.
              Westminster's busiest classified routes show genuine improvement in published survey data.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (6km — 1.8% of network): improving</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">7%</td>
                    <td className="px-4 py-2 text-center font-bold">13%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">79%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B and C roads are a tiny slice of Westminster — just 6km — but the trend is positive: green-condition
              share up from 70% to 79%, amber down from 23% to 13%.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (285km — 84% of network): RED share rising again</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center text-emerald-600 font-medium">4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-emerald-600 font-medium">4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Westminster publishes only the RED percentage for U-roads — not amber or green splits. At 6% in 2024,
              roughly 17km of the borough's residential and estate streets sit in the category the council's own
              data defines as "should be considered for maintenance."
            </p>
          </div>

          <QuoteCard
            quote="From 2026/27 a new methodology will be used based on the BSI PAS2161 standard. Local Highway Authorities will be required to use a supplier that has been accredited against PAS2161. This new standard will categorise roads into five categories instead of three to help government gain a more detailed understanding of road condition in England."
            source="Westminster City Council Local Highways Maintenance Transparency Report (25 June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five years of spending — and a GREEN scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue maintenance figures from Westminster's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">N/A</td>
                  <td className="px-4 py-3 text-center">£11,625k</td>
                  <td className="px-4 py-3 text-center">£4,005k</td>
                  <td className="px-4 py-3 text-center">74%</td>
                  <td className="px-4 py-3 text-center">26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">N/A</td>
                  <td className="px-4 py-3 text-center">£7,143k</td>
                  <td className="px-4 py-3 text-center">£3,394k</td>
                  <td className="px-4 py-3 text-center">68%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">N/A</td>
                  <td className="px-4 py-3 text-center">£14,487k</td>
                  <td className="px-4 py-3 text-center">£3,092k</td>
                  <td className="px-4 py-3 text-center">82%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£161k</td>
                  <td className="px-4 py-3 text-center">£20,223k</td>
                  <td className="px-4 py-3 text-center">£3,717k</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£185k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£22,566k</td>
                  <td className="px-4 py-3 text-center">£4,217k</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold">£523k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£17,957k</td>
                  <td className="px-4 py-3 text-center font-bold">£3,362k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">84%</td>
                  <td className="px-4 py-3 text-center font-bold">16%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-2xl font-bold mb-4">34 times the DfT allocation</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£523k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26 (projected)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£17.957m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">84%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2025/26</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Westminster projects capital spend at roughly{' '}
              <span className="text-emerald-400">34 times its DfT allocation</span> in 2025/26 — and earned a
              GREEN spend scorecard. That makes aggregate underfunding arguments harder. Your claim still turns
              on the specific defect, not the council's overall chequebook.
            </p>
          </div>

          <QuoteCard
            quote="A formal process that enables visual, engineering and end user considerations to be used in a transparent, auditable and objective manner for the identification, assessment and prioritisation of highway maintenance needs."
            source="Westminster City Council — Value Management definition in Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,663 potholes filled in four years
          </h2>
          <p className="text-surface-600 mb-8">
            Published pothole repair counts — a compact network that still produces hundreds of defects annually
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">395</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">387</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">525</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">356</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,663</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Peak then pullback</h3>
              <p className="text-surface-600 text-sm">
                Pothole fills peaked at 525 in 2023/24 — roughly 1.4 repairs every day — before falling to 356
                in 2024/25. On a 339km network, that is still nearly one published pothole repair daily. Defects
                form between inspections even on a well-funded authority.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Beyond the pothole table</h3>
              <p className="text-amber-800 text-sm">
                Westminster runs safety and service inspections simultaneously at risk-based frequencies determined
                by its Management Hierarchy. It has also introduced a Minor Works programme for clusters of defects
                to better utilise revenue budget. The published pothole count may not capture the full reactive workload.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection and VM */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspection, Value Management and planned works
          </h2>
          <p className="text-surface-600 mb-8">
            How Westminster says it finds, ranks and treats defects — in its own words
          </p>

          <QuoteCard
            quote="Our well-developed Value Management process combines the experience and expertise of our inspectorate team, independent DVI surveys in the footway and AI carriageway condition assessments. This information is then analysed with defect densities and street hierarchy profiles to ensure we are targeting those streets most in need of works on our PPM programme."
            source="Westminster City Council Local Highways Maintenance Transparency Report (25 June 2025)"
          />

          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 carriageway programme</h3>
              <p className="text-surface-600 text-sm">
                Westminster lists dozens of 2025/26 carriageway schemes with Value Management scores and costs — from
                Curzon Street (£42,000, VM 95.00) to Oliphant Street (£28,000, VM 29.00). Additional DfT funding brings
                forward Hobart Place (£139,000), Graham Terrace (£90,000), Great Titchfield Street (£196,000) and
                Inverness Terrace (£138,000). If your street was not listed, Westminster may argue it prioritised
                higher-scoring routes.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Streetworks coordination</h3>
              <p className="text-amber-800 text-sm">
                Westminster assesses every statutory undertaker permit independently and reviews road closures for
                coordination opportunities. If your pothole formed near recent utility reinstatement, ask whether the
                council enforced streetworks compliance on that route.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a well-funded AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Westminster invests heavily and documents its processes — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projects £17.957m capital against £523k DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 84% preventative maintenance share in 2024/25 and 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road green condition up from 70% to 77% since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C-road green condition up from 70% to 79%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented WMHI-aligned Value Management and HMMS data
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on recently treated classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED share back at 6% after falling to 4%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 84% of the network is U-roads where only RED percentages are published
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 525 pothole repairs in 2023/24 — defects still forming at scale
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice despite published HIAMP and VM framework
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> PAS 2161 methodology change from 2026/27 may complicate historical comparisons
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and detailed asset management, your claim lives or dies on the
              <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council Report It) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 6% borough-wide RED rate and 285km network share are your structural arguments</li>
              <li>• Whether your street appeared on the 2025/26 PPM carriageway programme — and whether works happened in time</li>
              <li>• Proximity to recent streetworks or utility reinstatement</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Westminster's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Report a pothole to Westminster
          </h2>
          <p className="text-surface-600 mb-8">
            Reporting the defect creates a council record — useful evidence if you later claim for damage
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Use the council's Report It service</h3>
              <p className="text-surface-600 text-sm mb-4">
                Westminster City Council accepts highway defect reports through its Report It portal. Pin the
                location on a map, describe the problem and upload a photo. The council states it will investigate
                confirmed reports.
              </p>
              <a
                href="https://report.westminster.gov.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
              >
                Report a pothole — Westminster Report It
                <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why reporting helps your claim</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Creates a dated council record of the defect before you submit a damage claim</li>
                <li>• A prior report can establish actual notice — weakening a Section 58 defence</li>
                <li>• Photos uploaded at report time document the defect's size and condition</li>
                <li>• Even if you already hit the pothole, reporting prevents the next driver being harmed</li>
              </ul>
              <p className="text-amber-800 text-sm mt-4">
                Westminster's transparency report describes safety inspections at risk-based frequencies — but
                inspections and reports are different. A report you or another road user filed is direct evidence
                the council knew about the defect.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Westminster?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road condition data cited</li>
                <li>• No prior-report search</li>
                <li>• No PPM programme cross-check</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 285km U-road network documented</li>
                <li>✅ 1,663 four-year pothole repairs cited</li>
                <li>✅ 34× DfT spend ratio referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Westminster</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=westminster"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Westminster claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Westminster projects capital spend at 34 times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. GREEN spend does not block a claim. Section 58 turns on whether the specific defect that damaged
                your vehicle was reasonably inspected and repaired — not on aggregate capital investment.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads are 285km — 84% of the network. Westminster's 2024 survey puts 6% of U-roads in RED condition,
                roughly 17km of residential streets that should be considered for maintenance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell from 525 to 356 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Westminster still filled 356 potholes in 2024/25 and runs risk-based safety inspections plus a
                Minor Works programme for defect clusters. A lower table count does not prove your defect was repaired in time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is best practice AMBER when Westminster publishes a HIAMP and Value Management process?
              </h3>
              <p className="text-surface-600 text-sm">
                Published process and DfT scorecards measure different things. Focus your claim on the specific defect
                and inspection records around it, not policy documents alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Was my road on Westminster's 2025/26 planned carriageway programme?
              </h3>
              <p className="text-surface-600 text-sm">
                Check the published programme in the transparency report. Listed streets had scheduled works and VM scores;
                unlisted streets may have been deprioritised — a relevant question for Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Westminster maintain every road in the borough?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Westminster's HIAMP excludes TfL-maintained and private roads. Check who maintains your road before
                you claim — red routes go to TfL, not the borough.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How long do I have to make a claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Three years from the incident under the Limitation Act 1980 — but claim within three months where possible
                while evidence is fresh.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.westminster.gov.uk/roads-and-travel/roads-and-highways/highways-planned-preventative-maintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Westminster City Council Local Highways Maintenance Transparency Report (25 June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
